import { ArrowDownTrayIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Headline,
  Input,
  Paragraph,
  Select,
  type SelectItem,
  Small,
} from "@fronterahealth/frontera-ui-components";

import {
  type RecentExport,
  type RecentExportOverrides,
  getExportTestStatus,
  listRecentExports,
  startExportTest,
} from "@api/rest/export-testing";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ExportFramework, type ExportFrameworkValue } from "@pages/Templates/constants";

import {
  ExportDocType,
  type ExportDocTypeValue,
  ExportFont,
  type ExportFontValue,
  ExportWorkflowStatus,
  type ExportWorkflowStatusValue,
  isValidReportId,
} from "./constants";

const DOCTYPE_ITEMS: SelectItem[] = [
  { id: ExportDocType.PDF, primary: "PDF" },
  { id: ExportDocType.DOCX, primary: "DOCX" },
];

const FRAMEWORK_ITEMS: SelectItem[] = [
  { id: ExportFramework.GOOGLE_DOCS, primary: "Google Docs" },
  { id: ExportFramework.HANDLEBARS, primary: "Handlebars" },
  { id: ExportFramework.DOCXTEMPLATER, primary: "Docxtemplater" },
];

const FONT_ITEMS: SelectItem[] = [
  { id: ExportFont.ARIAL, primary: "Arial" },
  { id: ExportFont.CALIBRI, primary: "Calibri" },
  { id: ExportFont.CENTURY_GOTHIC, primary: "Century Gothic" },
  { id: ExportFont.GEORGIA, primary: "Georgia" },
  { id: ExportFont.PUBLIC_SANS, primary: "Public Sans" },
  { id: ExportFont.TIMES_NEW_ROMAN, primary: "Times New Roman" },
];

const FAST_REFRESH_MS = 2000;
const SLOW_REFRESH_MS = 15000;

export const ExportTesting: FC = () => {
  const [reportId, setReportId] = useState("");
  const [doctype, setDoctype] = useState<ExportDocTypeValue>(ExportDocType.PDF);
  const [framework, setFramework] = useState<ExportFrameworkValue | "">("");
  const [templateUrl, setTemplateUrl] = useState("");
  const [templateLogoUrl, setTemplateLogoUrl] = useState("");
  const [orgLogoUrl, setOrgLogoUrl] = useState("");
  const [font, setFont] = useState<ExportFontValue | "">("");
  const [reportIdError, setReportIdError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [recentExports, setRecentExports] = useState<RecentExport[]>([]);
  const [refreshingRecent, setRefreshingRecent] = useState(false);
  // Workflows we've just started that haven't yet appeared in the recent list.
  // Keeps polling in fast mode during the window between start and first sighting.
  const [pendingWorkflowIds, setPendingWorkflowIds] = useState<Set<string>>(() => new Set());

  // Tracks the last-seen status per workflow_id so we can fire a notification
  // when a row transitions out of RUNNING between refreshes.
  const prevStatusRef = useRef<Map<string, ExportWorkflowStatusValue> | null>(null);

  const refreshRecent = useCallback(async () => {
    try {
      const rows = await listRecentExports();
      const startedAtMs = (r: RecentExport) => {
        const t = Date.parse(r.started_at);
        return Number.isNaN(t) ? 0 : t;
      };
      const sorted = [...rows].sort((a, b) => startedAtMs(b) - startedAtMs(a));
      const prev = prevStatusRef.current;
      if (prev) {
        for (const r of sorted) {
          if (prev.get(r.workflow_id) === ExportWorkflowStatus.RUNNING) {
            if (r.status === ExportWorkflowStatus.COMPLETE) {
              notifySuccess("Export complete");
            } else if (r.status === ExportWorkflowStatus.FAILED) {
              notifyError("Export failed");
            }
          }
        }
      }
      const next = new Map<string, ExportWorkflowStatusValue>();
      for (const r of sorted) next.set(r.workflow_id, r.status);
      prevStatusRef.current = next;
      setRecentExports(sorted);
      setPendingWorkflowIds((prev) => {
        if (prev.size === 0) return prev;
        const remaining = new Set(prev);
        for (const r of sorted) remaining.delete(r.workflow_id);
        return remaining.size === prev.size ? prev : remaining;
      });
    } catch {
      /* ignore transient errors */
    }
  }, []);

  const manualRefresh = useCallback(async () => {
    setRefreshingRecent(true);
    try {
      await refreshRecent();
    } finally {
      setRefreshingRecent(false);
    }
  }, [refreshRecent]);

  const anyRunning = useMemo(
    () => pendingWorkflowIds.size > 0 || recentExports.some((r) => r.status === ExportWorkflowStatus.RUNNING),
    [recentExports, pendingWorkflowIds],
  );

  useEffect(() => {
    const initial = setTimeout(() => void refreshRecent(), 0);
    const interval = anyRunning ? FAST_REFRESH_MS : SLOW_REFRESH_MS;
    const iv = setInterval(() => void refreshRecent(), interval);
    return () => {
      clearTimeout(initial);
      clearInterval(iv);
    };
  }, [refreshRecent, anyRunning]);

  const onReportIdBlur = useCallback(() => {
    if (!reportId.trim()) {
      setReportIdError(null);
      return;
    }
    setReportIdError(isValidReportId(reportId) ? null : "Must be a positive integer or a base64-encoded global ID.");
  }, [reportId]);

  const canSubmit = !submitting && reportId.trim().length > 0 && isValidReportId(reportId) && !!doctype;

  const onSubmit = useCallback(async () => {
    if (!isValidReportId(reportId)) {
      setReportIdError("Must be a positive integer or a base64-encoded global ID.");
      return;
    }
    setSubmitting(true);
    try {
      const { workflow_id } = await startExportTest({
        report_id: reportId.trim(),
        doctype,
        export_framework: framework || null,
        template_url: templateUrl.trim() || null,
        template_logo_url: templateLogoUrl.trim() || null,
        org_logo_url: orgLogoUrl.trim() || null,
        font: font || null,
      });
      setPendingWorkflowIds((prev) => {
        const next = new Set(prev);
        next.add(workflow_id);
        return next;
      });
      void refreshRecent();
    } catch (err) {
      notifyError("Failed to start export workflow");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [reportId, doctype, framework, templateUrl, templateLogoUrl, orgLogoUrl, font, refreshRecent]);

  const doctypeSelected = DOCTYPE_ITEMS.find((i) => i.id === doctype) ?? null;
  const frameworkSelected = FRAMEWORK_ITEMS.find((i) => i.id === framework) ?? null;
  const fontSelected = FONT_ITEMS.find((i) => i.id === font) ?? null;

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex items-end justify-between px-16 pb-4 pt-8 shrink-0">
        <Headline size="medium">Export Testing</Headline>
      </div>

      <div className="px-16 pb-16 grid grid-cols-12 gap-8 w-full">
        {/* Left: form */}
        <section className="col-span-7 flex flex-col gap-6">
          <div className="rounded-md border border-border-subtle bg-bg-surface p-6 flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <Headline size="small">Inputs</Headline>
              <Small className="text-text-secondary">
                Provide a report ID and an output format. The base64 form is the same Relay global ID used elsewhere in
                FDM.
              </Small>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-secondary">Report ID</label>
              <Input
                name="report_id"
                label=""
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                onBlur={onReportIdBlur}
                placeholder="89 or UmVwb3J0Ojg5"
                inputSize="full"
                errorMessage={reportIdError ?? ""}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-secondary">Document type</label>
              <Select
                title=""
                items={DOCTYPE_ITEMS}
                selected={doctypeSelected}
                setSelected={(item) => setDoctype(String(item.id) as ExportDocTypeValue)}
                placeholderText="Select document type"
              />
            </div>

            <Small className="text-text-secondary">
              Output is written to a temp bucket and the download link expires after 10 minutes.
            </Small>
          </div>

          <div className="rounded-md border border-border-subtle bg-bg-surface p-6 flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <Headline size="small">Overrides</Headline>
              <Small className="text-text-secondary">
                Leave blank to use the report's existing template configuration.
              </Small>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-secondary">Export framework</label>
              <Select
                title=""
                items={FRAMEWORK_ITEMS}
                selected={frameworkSelected}
                setSelected={(item) => setFramework(String(item.id) as ExportFrameworkValue)}
                placeholderText="Use template default"
                actionItem={
                  framework
                    ? {
                        label: "Clear",
                        onClick: () => setFramework(""),
                      }
                    : undefined
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-secondary">Font</label>
              <Select
                title=""
                items={FONT_ITEMS}
                selected={fontSelected}
                setSelected={(item) => setFont(String(item.id) as ExportFontValue)}
                placeholderText="Use template default"
                actionItem={
                  font
                    ? {
                        label: "Clear",
                        onClick: () => setFont(""),
                      }
                    : undefined
                }
              />
            </div>

            <OverrideInput
              label="Template URL"
              value={templateUrl}
              setValue={setTemplateUrl}
              placeholder="https://docs.google.com/document/d/..."
            />
            <OverrideInput
              label="Template logo URL"
              value={templateLogoUrl}
              setValue={setTemplateLogoUrl}
              placeholder="https://..."
            />
            <OverrideInput
              label="Organization logo URL"
              value={orgLogoUrl}
              setValue={setOrgLogoUrl}
              placeholder="https://..."
            />
          </div>

          <div>
            <Button
              text={submitting ? "Starting…" : "Run export"}
              appearance="primary"
              onClick={onSubmit}
              disabled={!canSubmit}
            />
          </div>
        </section>

        {/* Right: merged runs list */}
        <section className="col-span-5 flex flex-col gap-6">
          <RunsCard rows={recentExports} refreshing={refreshingRecent} onRefresh={() => void manualRefresh()} />
        </section>
      </div>
    </div>
  );
};

interface OverrideInputProps {
  label: string;
  value: string;
  setValue: (v: string) => void;
  placeholder?: string;
}

const OverrideInput: FC<OverrideInputProps> = ({ label, value, setValue, placeholder }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-medium text-text-secondary">{label}</label>
    <Input
      name={label}
      label=""
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder={placeholder}
      inputSize="full"
    />
  </div>
);

interface RunsCardProps {
  rows: RecentExport[];
  refreshing: boolean;
  onRefresh: () => void;
}

const RunsCard: FC<RunsCardProps> = ({ rows, refreshing, onRefresh }) => (
  <div className="rounded-md border border-border-subtle bg-bg-surface p-6 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <Headline size="small">Runs (last 15m)</Headline>
      <button
        type="button"
        onClick={onRefresh}
        className="text-text-secondary hover:text-text-primary"
        title="Refresh"
        aria-label="Refresh runs"
      >
        <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
      </button>
    </div>
    {rows.length === 0 ? (
      <Paragraph className="text-text-secondary">No export runs in the last 15 minutes.</Paragraph>
    ) : (
      <ul className="flex flex-col divide-y divide-border-subtle">
        {rows.map((r) => (
          <li key={r.workflow_id} className="py-2 flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-text-primary break-all">{r.workflow_id}</span>
              <StatusBadge status={r.status} />
            </div>
            <span className="text-xs text-text-secondary">Started {new Date(r.started_at).toLocaleTimeString()}</span>
            <RunInputDetails reportId={r.report_id} doctype={r.doctype} overrides={r.overrides} />
            {r.status === ExportWorkflowStatus.COMPLETE && <RecentDownloadButton workflowId={r.workflow_id} />}
            {r.status === ExportWorkflowStatus.FAILED && <RunErrorMessage workflowId={r.workflow_id} />}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const RecentDownloadButton: FC<{ workflowId: string }> = ({ workflowId }) => {
  const [loading, setLoading] = useState(false);
  const onClick = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExportTestStatus(workflowId);
      if (res.status === ExportWorkflowStatus.COMPLETE && res.download_url) {
        window.open(res.download_url, "_blank", "noopener");
      } else {
        notifyError("No download URL available — file may have expired.");
      }
    } catch {
      notifyError("Failed to fetch download URL");
    } finally {
      setLoading(false);
    }
  }, [workflowId]);
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary disabled:opacity-60"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        {loading ? "Fetching…" : "Download"}
      </button>
    </div>
  );
};

const RunErrorMessage: FC<{ workflowId: string }> = ({ workflowId }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getExportTestStatus(workflowId);
        if (!cancelled) setMessage(res.error_message ?? "Workflow failed without a message");
      } catch {
        if (!cancelled) setMessage("Failed to fetch error details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowId]);
  if (loading) return <Small className="text-text-secondary">Loading error…</Small>;
  if (!message) return null;
  return <Small className="text-text-error-primary break-all">{message}</Small>;
};

interface RunInputDetailsProps {
  reportId: string | null;
  doctype: string | null;
  overrides: RecentExportOverrides | null;
}

const RunInputDetails: FC<RunInputDetailsProps> = ({ reportId, doctype, overrides }) => {
  const overrideEntries = overrides
    ? (Object.entries(overrides).filter(([, v]) => v != null && v !== "") as [keyof RecentExportOverrides, string][])
    : [];
  if (!reportId && !doctype && overrideEntries.length === 0) return null;
  return (
    <div className="mt-1 flex flex-col gap-0.5 text-xs text-text-secondary">
      {(reportId || doctype) && (
        <span>
          {reportId && (
            <>
              <span className="text-text-tertiary">report:</span>{" "}
              <span className="text-text-primary break-all">{reportId}</span>
            </>
          )}
          {reportId && doctype && (
            <>
              {" "}
              <span className="text-text-tertiary">·</span>{" "}
            </>
          )}
          {doctype && (
            <>
              <span className="text-text-tertiary">doctype:</span> <span className="text-text-primary">{doctype}</span>
            </>
          )}
        </span>
      )}
      {overrideEntries.map(([k, v]) => (
        <span key={k} className="break-all">
          <span className="text-text-tertiary">{k}:</span> <span className="text-text-primary">{v}</span>
        </span>
      ))}
    </div>
  );
};

const StatusBadge: FC<{ status: ExportWorkflowStatusValue }> = ({ status }) => {
  const styles: Record<ExportWorkflowStatusValue, string> = {
    [ExportWorkflowStatus.RUNNING]: "bg-bg-default text-limestone-700",
    [ExportWorkflowStatus.COMPLETE]: "bg-bg-default text-text-primary",
    [ExportWorkflowStatus.FAILED]: "bg-bg-default text-text-error-primary",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
};
